import React, { useContext, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import {
    Button,
    Checkbox,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Tag,
    Textarea,
} from '@onward-delivery/design';
import { Icon, Info, RefreshCw, Trash2 } from '@onward-delivery/design/icons';
import { cloneDeep, get, set } from 'lodash';
import * as Sentry from '@sentry/react';
import { post } from '@/utilities/onwardClient';
import { UPLOAD_INTEGRATION_SECRET } from '@/constants/apiRoutes';
import { INTEGRATION_CONFIG_KEYS } from '@onward-delivery/core';
import { INTEGRATION_TYPE_LABELS, INTEGRATION_CONFIG_FIELDS, SHIPPER_SCOPED_OVERRIDE_TYPES } from './constants';
import { CopyableId } from './blocks';
import { CorpAdminContext } from '../store';
import { CORP_INTEGRATION_QUERIES, DELETE_CORP_INTEGRATION_OVERRIDE, GET_CORP_INTEGRATION_OVERRIDES } from '../graphql';
import { UPDATE_INTEGRATION_OVERRIDE_BY_PK } from '@/graphql/mutations/integration_overrides';
import { useClientUser } from '@/hooks';

const INTEGRATION_BLOCK_CLASS = 'flex flex-col gap-4 rounded-lg border border-slate-200 p-4';
const ROW_HEADER_CLASS = 'flex flex-wrap items-center gap-3';
const STATUS_TAG_VARIANTS = { COMPLETE: 'primary', PENDING: 'warning' };
const STALE_LIST_MESSAGE = 'Integration saved, but the list could not refresh. Reload to see it.';

const InlineLabel = ({ label, value }) => (
    <span className="text-[13px] text-slate-500">
        {label}: <span className="font-medium text-slate-900">{value}</span>
    </span>
);

// A native title, not the library Tooltip: this row also renders outside a TooltipProvider (PC Miler setup).
const FieldLabel = ({ label, tooltip, children }) => (
    <label className="flex flex-col gap-1">
        <span className="flex items-center gap-1 text-[12px] font-medium text-slate-600">
            {label}
            {tooltip ? (
                <span title={tooltip} className="cursor-help text-slate-400">
                    <Icon icon={Info} size="sm" className="size-3.5" />
                </span>
            ) : null}
        </span>
        {children}
    </label>
);

const PrefixedInput = ({ prefix, ...inputProps }) => (
    <div className="flex items-center gap-2">
        <span className="text-[13px] text-slate-500">{prefix}</span>
        <Input {...inputProps} />
    </div>
);

const getFieldPath = (field) => field.path || [field.key];
const getFieldDraft = (field, configDraft, defaultOrderDraft) =>
    field.target === 'default_order' ? defaultOrderDraft : configDraft;

// For fields with a fixed prefix (e.g. PC Miler `PCM`): the user types only the suffix while
// the full prefixed value is stored. Derives the editable suffix from the stored value.
const stripPrefix = (prefix, value) => String(value ?? '').replace(new RegExp(`^\\s*${prefix}\\s*`, 'i'), '');

const setObjectPath = (setter, path, value) =>
    setter((prev) => {
        const next = cloneDeep(prev);
        set(next, path, value);
        return next;
    });

const DeleteIntegrationButton = ({ integration, orgShipperIds, onDeleted, onError }) => {
    const [deleteIntegration, { loading: deleting }] = useMutation(DELETE_CORP_INTEGRATION_OVERRIDE, {
        update(cache, { data }) {
            const deletedId = data?.deleted_override?.override_id;
            if (!deletedId) return;

            const existing = cache.readQuery({
                query: GET_CORP_INTEGRATION_OVERRIDES,
                variables: { orgShipperIds },
            });
            if (!existing) return;

            cache.writeQuery({
                query: GET_CORP_INTEGRATION_OVERRIDES,
                variables: { orgShipperIds },
                data: {
                    integration_overrides: existing.integration_overrides.filter((o) => o.override_id !== deletedId),
                },
            });
        },
    });

    const isOrgDefault = !!integration.organization_id;
    const confirmMessage = isOrgDefault
        ? 'Delete this default? Every shipper using it loses the integration.'
        : 'Delete this integration?';

    const handleDelete = async (e) => {
        e?.stopPropagation?.();
        if (!window.confirm(confirmMessage)) return;
        try {
            const hookIds = (integration.subscriptions || []).map((s) => s.event_subscription_id).filter(Boolean);
            await deleteIntegration({
                variables: { override_id: integration.override_id, hook_ids: hookIds },
                refetchQueries: isOrgDefault ? CORP_INTEGRATION_QUERIES : [],
            });
            onDeleted?.();
        } catch (err) {
            Sentry.captureException(err);
            onError?.(err);
        }
    };

    return (
        <Button
            variant="outline"
            size="sm"
            aria-label="Delete integration"
            title="Delete integration"
            onClick={handleDelete}
            disabled={deleting}
            className="ml-auto border-red-200 px-2 text-red-600"
        >
            <Icon icon={Trash2} size="sm" className="size-4" />
        </Button>
    );
};

const DiscardDraftButton = ({ onDiscard }) => (
    <Button variant="outline" size="sm" onClick={onDiscard} className="ml-auto">
        Discard
    </Button>
);

const ManualSetupRow = ({ integration, onSaved, onDiscard, onWritten }) => {
    const apolloClient = useApolloClient();
    const { state, callbacks } = useContext(CorpAdminContext);
    const { orgShipperIds } = state;
    const { setNotification } = callbacks;

    const [pickedType, setPickedType] = useState('');
    const [draft, setDraft] = useState({});
    const [defaultOrderDraft, setDefaultOrderDraft] = useState({});
    const [secrets, setSecrets] = useState({});
    const [jsonbText, setJsonbText] = useState({});
    const [saving, setSaving] = useState(false);

    const updateCache = (savedOverride) => {
        if (!savedOverride?.override_id) return;

        const existing = apolloClient.readQuery({
            query: GET_CORP_INTEGRATION_OVERRIDES,
            variables: { orgShipperIds },
        });
        if (!existing) return;

        apolloClient.writeQuery({
            query: GET_CORP_INTEGRATION_OVERRIDES,
            variables: { orgShipperIds },
            data: {
                integration_overrides: existing.integration_overrides.map((o) =>
                    o.override_id === savedOverride.override_id ? { ...o, ...savedOverride } : o
                ),
            },
        });
    };

    const fields = INTEGRATION_CONFIG_FIELDS[pickedType] || [];

    const resetForType = (nextType) => {
        setPickedType(nextType);
        setDraft({});
        setDefaultOrderDraft({});
        setSecrets({});
        setJsonbText({});
    };

    const setFieldValue = (field, value) =>
        setObjectPath(field.target === 'default_order' ? setDefaultOrderDraft : setDraft, getFieldPath(field), value);

    const getFieldValue = (field) => get(getFieldDraft(field, draft, defaultOrderDraft), getFieldPath(field));

    const handleJsonbChange = (field, text) => {
        const { key } = field;
        setJsonbText((prev) => ({ ...prev, [key]: text }));
        try {
            const parsed = text.trim() ? JSON.parse(text) : null;
            setFieldValue(field, parsed);
        } catch {
            // invalid — leave draft as-is until valid
        }
    };

    const isJsonbValid = (key) => {
        const text = jsonbText[key] ?? '';
        if (!text.trim()) return true;
        try {
            JSON.parse(text);
            return true;
        } catch {
            return false;
        }
    };

    const anyInvalidJsonb = fields.some((f) => f.type === 'jsonb' && !isJsonbValid(f.key));

    const handleSave = async () => {
        setSaving(true);
        try {
            const secretEntries = fields
                .filter((f) => f.secret)
                .map((f) => ({ key: f.key, value: secrets[f.key] }))
                .filter((s) => s.value && s.value.trim());

            const body = {
                override: {
                    override_id: integration.override_id,
                    carrier_id: integration.carrier_id,
                    shipper_id: integration.shipper_id,
                    client_id: integration.client_id,
                    middle_mile_carrier_id: integration.middle_mile_carrier_id,
                    integration_meta_type_id: integration.integration_meta_type_id,
                    organization_id: integration.organization_id,
                    type: pickedType,
                    config: draft,
                    default_order: defaultOrderDraft,
                },
                secrets: secretEntries,
            };
            const result = await post(UPLOAD_INTEGRATION_SECRET, body);
            updateCache(result?.data?.data);
            // Shippers riding a default show its type, so both lists refresh.
            try {
                await apolloClient.refetchQueries({ include: CORP_INTEGRATION_QUERIES });
            } catch (refetchError) {
                Sentry.captureException(refetchError);
                setNotification?.({ severity: 'warning', message: STALE_LIST_MESSAGE });
                return;
            } finally {
                onSaved?.();
            }
            setNotification?.({ severity: 'success', message: 'Integration set up' });
        } catch (err) {
            // Log details for diagnostics; show only a generic message to the user.
            console.error('Integration setup failed', err?.response?.data || err);
            const writtenOverrideId = err?.response?.data?.override_id;
            if (writtenOverrideId) onWritten?.(writtenOverrideId);
            Sentry.captureException(err);
            setNotification?.({ severity: 'error', message: 'Failed to set up integration' });
        } finally {
            setSaving(false);
        }
    };

    const fieldControls = fields.map((f) => {
        if (f.type === 'integer') {
            const val = getFieldValue(f);
            const display = val !== undefined && val !== null ? String(val) : '';
            return (
                <FieldLabel key={f.key} label={f.label} tooltip={f.tooltip}>
                    <Input
                        type="number"
                        step={1}
                        value={display}
                        onChange={(e) => {
                            const raw = e.target.value;
                            const parsed = raw === '' ? null : parseInt(raw, 10);
                            setFieldValue(f, Number.isNaN(parsed) ? null : parsed);
                        }}
                    />
                </FieldLabel>
            );
        }

        if (f.type === 'jsonb') {
            const text = jsonbText[f.key] ?? '';
            const valid = isJsonbValid(f.key);
            return (
                <FieldLabel key={f.key} label={f.label} tooltip={f.tooltip}>
                    <Textarea
                        rows={3}
                        value={text}
                        onChange={(e) => handleJsonbChange(f, e.target.value)}
                        error={!valid}
                        className="font-mono text-xs"
                    />
                    {!valid ? <span className="text-[12px] text-destructive">Invalid JSON</span> : null}
                </FieldLabel>
            );
        }

        if (f.type === 'boolean') {
            return (
                <label key={f.key} className="flex items-center gap-2 text-[13px] text-slate-700">
                    <Checkbox
                        checked={!!getFieldValue(f)}
                        onCheckedChange={(checked) => setFieldValue(f, checked === true)}
                    />
                    {f.label}
                </label>
            );
        }

        if (f.secret) {
            return (
                <FieldLabel key={f.key} label={f.label} tooltip={f.tooltip}>
                    <Input
                        type="password"
                        autoComplete="new-password"
                        value={secrets[f.key] ?? ''}
                        onChange={(e) => setSecrets((prev) => ({ ...prev, [f.key]: e.target.value }))}
                    />
                </FieldLabel>
            );
        }

        if (f.prefix) {
            return (
                <FieldLabel key={f.key} label={f.label} tooltip={f.tooltip}>
                    <PrefixedInput
                        prefix={f.prefix}
                        value={stripPrefix(f.prefix, getFieldValue(f))}
                        onChange={(e) => {
                            const rest = stripPrefix(f.prefix, e.target.value);
                            setFieldValue(f, rest ? `${f.prefix}${rest}` : '');
                        }}
                    />
                </FieldLabel>
            );
        }

        return (
            <FieldLabel key={f.key} label={f.label} tooltip={f.tooltip}>
                <Input
                    value={get(getFieldDraft(f, draft, defaultOrderDraft), getFieldPath(f)) ?? ''}
                    onChange={(e) => setFieldValue(f, e.target.value)}
                />
            </FieldLabel>
        );
    });

    // A type without fields here would save an empty config, so it waits until its fields are copied over.
    const typeOptions = Object.keys(INTEGRATION_CONFIG_FIELDS).filter(
        (type) =>
            !SHIPPER_SCOPED_OVERRIDE_TYPES.includes(type) &&
            (!integration.is_shipper_default || INTEGRATION_CONFIG_KEYS[type]?.selectable)
    );

    const removeButton = onDiscard ? (
        <DiscardDraftButton onDiscard={onDiscard} />
    ) : (
        <DeleteIntegrationButton
            integration={integration}
            orgShipperIds={orgShipperIds}
            onDeleted={() => setNotification?.({ severity: 'success', message: 'Integration deleted' })}
            onError={() => setNotification?.({ severity: 'error', message: 'Failed to delete integration' })}
        />
    );

    return (
        <div className={INTEGRATION_BLOCK_CLASS}>
            <div className={ROW_HEADER_CLASS}>
                <CopyableId label="Integration ID" value={integration.override_id} />
                <Tag variant="warning">Needs Setup</Tag>
                {removeButton}
            </div>
            <FieldLabel label="Type">
                <Select value={pickedType} onValueChange={resetForType}>
                    <SelectTrigger aria-label="Type">
                        <SelectValue placeholder="Select a type" />
                    </SelectTrigger>
                    <SelectContent>
                        {typeOptions.map((value) => (
                            <SelectItem key={value} value={value}>
                                {INTEGRATION_TYPE_LABELS[value] || value}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </FieldLabel>
            {pickedType && fields.length > 0 ? <div className="flex flex-col gap-3">{fieldControls}</div> : null}
            <div>
                <Button loading={saving} disabled={!pickedType || saving || anyInvalidJsonb} onClick={handleSave}>
                    Save
                </Button>
            </div>
        </div>
    );
};

const ConfiguredIntegrationRow = ({ integration, onSaved, onDiscard, onWritten }) => {
    const { isOnwardEngineer } = useClientUser();
    const client = useApolloClient();
    const { state, callbacks } = useContext(CorpAdminContext);
    const { orgShipperIds } = state;
    const { setNotification } = callbacks;

    const [markComplete, { loading: markingComplete }] = useMutation(UPDATE_INTEGRATION_OVERRIDE_BY_PK, {
        onError: (e) => {
            Sentry.captureException(e);
            setNotification?.({ severity: 'error', message: 'Failed to mark integration complete' });
        },
        onCompleted: () => setNotification?.({ severity: 'success', message: 'Integration marked complete' }),
    });

    const [configDraft, setConfigDraft] = useState(() => cloneDeep(integration.config || {}));
    const [defaultOrderDraft, setDefaultOrderDraft] = useState(() => cloneDeep(integration.default_order || {}));
    const [saving, setSaving] = useState(false);
    const [secrets, setSecrets] = useState({});

    // Raw text state for jsonb fields — kept in sync with configDraft only when valid JSON.
    const [jsonbText, setJsonbText] = useState(() => {
        const init = {};
        const configFields = INTEGRATION_CONFIG_FIELDS[integration.type] || [];
        configFields
            .filter((f) => f.type === 'jsonb')
            .forEach((f) => {
                const val = get(
                    f.target === 'default_order' ? integration.default_order : integration.config,
                    getFieldPath(f)
                );
                init[f.key] = val !== undefined && val !== null ? JSON.stringify(val, null, 2) : '';
            });
        return init;
    });

    const setFieldValue = (field, value) =>
        setObjectPath(
            field.target === 'default_order' ? setDefaultOrderDraft : setConfigDraft,
            getFieldPath(field),
            value
        );

    const getFieldValue = (field) => get(getFieldDraft(field, configDraft, defaultOrderDraft), getFieldPath(field));

    const handleJsonbChange = (field, text) => {
        const { key } = field;
        setJsonbText((prev) => ({ ...prev, [key]: text }));
        try {
            const parsed = text.trim() ? JSON.parse(text) : null;
            setFieldValue(field, parsed);
        } catch {
            // Invalid JSON — don't update the persisted draft.
        }
    };

    const handleIntegerChange = (field, raw) => {
        const val = raw === '' ? null : parseInt(raw, 10);
        setFieldValue(field, Number.isNaN(val) ? null : val);
    };

    const isJsonbValid = (key) => {
        const text = jsonbText[key] ?? '';
        if (!text.trim()) return true;
        try {
            JSON.parse(text);
            return true;
        } catch {
            return false;
        }
    };

    const updateCache = (savedOverride) => {
        if (!savedOverride?.override_id) return;

        const existing = client.readQuery({
            query: GET_CORP_INTEGRATION_OVERRIDES,
            variables: { orgShipperIds },
        });
        if (!existing) return;

        client.writeQuery({
            query: GET_CORP_INTEGRATION_OVERRIDES,
            variables: { orgShipperIds },
            data: {
                integration_overrides: existing.integration_overrides.map((o) =>
                    o.override_id === savedOverride.override_id ? { ...o, ...savedOverride } : o
                ),
            },
        });
    };

    const callSaveFunction = async () => {
        setSaving(true);
        try {
            const secretEntries = Object.entries(secrets)
                .filter(([, v]) => v && v.trim())
                .map(([key, value]) => ({ key, value }));
            const body = {
                override: {
                    override_id: integration.override_id,
                    carrier_id: integration.carrier_id,
                    shipper_id: integration.shipper_id,
                    client_id: integration.client_id,
                    middle_mile_carrier_id: integration.middle_mile_carrier_id,
                    integration_meta_type_id: integration.integration_meta_type_id,
                    organization_id: integration.organization_id,
                    type: integration.type,
                    config: configDraft,
                    default_order: defaultOrderDraft,
                },
            };
            if (secretEntries.length > 0) body.secrets = secretEntries;
            const result = await post(UPLOAD_INTEGRATION_SECRET, body);
            const savedOverride = result?.data?.data;
            const updatedConfig = savedOverride?.config ?? configDraft;
            const updatedDefaultOrder = savedOverride?.default_order ?? defaultOrderDraft;
            setConfigDraft(cloneDeep(updatedConfig));
            setDefaultOrderDraft(cloneDeep(updatedDefaultOrder));
            setSecrets({});
            updateCache(savedOverride);
            // A draft isn't in the cached list yet, so it needs the refetch to show up.
            if (integration.organization_id || onSaved) {
                try {
                    await client.refetchQueries({ include: CORP_INTEGRATION_QUERIES });
                } catch (refetchError) {
                    Sentry.captureException(refetchError);
                    setNotification({ severity: 'warning', message: STALE_LIST_MESSAGE });
                    return;
                } finally {
                    onSaved?.();
                }
            }
            setNotification({ severity: 'success', message: 'Integration saved.' });
        } catch (err) {
            console.error('Integration save failed', err?.response?.data || err);
            const writtenOverrideId = err?.response?.data?.override_id;
            if (writtenOverrideId) onWritten?.(writtenOverrideId);
            Sentry.captureException(err);
            setNotification({ severity: 'error', message: 'Failed to save integration' });
        } finally {
            setSaving(false);
        }
    };

    const isEdi = !!(integration.rec_isa_id || integration.send_isa_id);
    const configFields = INTEGRATION_CONFIG_FIELDS[integration.type];

    const fieldControls = (configFields || []).map((field) => {
        const { key, label, secret, type, tooltip } = field;

        if (type === 'integer') {
            const currentVal = getFieldValue(field);
            const displayVal = currentVal !== undefined && currentVal !== null ? String(currentVal) : '';
            return (
                <FieldLabel key={key} label={label} tooltip={tooltip}>
                    <Input
                        type="number"
                        step={1}
                        value={displayVal}
                        onChange={(e) => handleIntegerChange(field, e.target.value)}
                    />
                </FieldLabel>
            );
        }

        if (type === 'jsonb') {
            const text = jsonbText[key] ?? '';
            const valid = isJsonbValid(key);
            return (
                <FieldLabel key={key} label={label} tooltip={tooltip}>
                    <Textarea
                        rows={3}
                        value={text}
                        onChange={(e) => handleJsonbChange(field, e.target.value)}
                        error={!valid}
                        className="font-mono text-xs"
                    />
                    {!valid ? <span className="text-[12px] text-destructive">Invalid JSON</span> : null}
                </FieldLabel>
            );
        }

        if (type === 'boolean') {
            return (
                <label key={key} className="flex items-center gap-2 text-[13px] text-slate-700">
                    <Checkbox
                        checked={!!getFieldValue(field)}
                        onCheckedChange={(checked) => setFieldValue(field, checked === true)}
                    />
                    {label}
                </label>
            );
        }

        if (secret) {
            return (
                <FieldLabel key={key} label={label} tooltip={tooltip}>
                    <Input
                        type="password"
                        autoComplete="new-password"
                        placeholder="Leave blank to keep existing"
                        value={secrets[key] ?? ''}
                        onChange={(e) => setSecrets((prev) => ({ ...prev, [key]: e.target.value }))}
                    />
                </FieldLabel>
            );
        }

        if (field.prefix) {
            return (
                <FieldLabel key={key} label={label} tooltip={tooltip}>
                    <PrefixedInput
                        prefix={field.prefix}
                        value={stripPrefix(field.prefix, getFieldValue(field))}
                        onChange={(e) => {
                            const rest = stripPrefix(field.prefix, e.target.value);
                            setFieldValue(field, rest ? `${field.prefix}${rest}` : '');
                        }}
                    />
                </FieldLabel>
            );
        }

        return (
            <FieldLabel key={key} label={label} tooltip={tooltip}>
                <Input value={getFieldValue(field) ?? ''} onChange={(e) => setFieldValue(field, e.target.value)} />
            </FieldLabel>
        );
    });

    const configBody = configFields ? (
        <div className="flex flex-col gap-3">{fieldControls}</div>
    ) : (
        <pre className="m-0 overflow-x-auto rounded bg-slate-50 p-2 text-[11px]">
            {JSON.stringify(integration.config, null, 2)}
        </pre>
    );

    const statusTag = integration.status ? (
        <Tag variant={STATUS_TAG_VARIANTS[integration.status] || 'destructive'}>{integration.status}</Tag>
    ) : null;

    const canMarkComplete = isOnwardEngineer && !onDiscard && integration.status !== 'COMPLETE';
    const removeButton = onDiscard ? (
        <DiscardDraftButton onDiscard={onDiscard} />
    ) : (
        <DeleteIntegrationButton
            integration={integration}
            orgShipperIds={orgShipperIds}
            onDeleted={() => setNotification?.({ severity: 'success', message: 'Integration deleted' })}
            onError={() => setNotification?.({ severity: 'error', message: 'Failed to delete integration' })}
        />
    );

    return (
        <div className={INTEGRATION_BLOCK_CLASS}>
            <div className={ROW_HEADER_CLASS}>
                <CopyableId label="Integration ID" value={integration.override_id} />
                <InlineLabel label="Type" value={INTEGRATION_TYPE_LABELS[integration.type] ?? integration.type} />
                {integration.poll ? (
                    <span title="Orders with this integration are only updated every 30-60min">
                        <Tag variant="info">
                            <Icon icon={RefreshCw} size="sm" className="mr-1 size-3" />
                            Polling
                        </Tag>
                    </span>
                ) : null}
                {integration.rec_isa_id ? <InlineLabel label="Rec ISA ID" value={integration.rec_isa_id} /> : null}
                {integration.send_isa_id ? <InlineLabel label="Send ISA ID" value={integration.send_isa_id} /> : null}
                {statusTag}
                {canMarkComplete ? (
                    <Button
                        size="sm"
                        disabled={markingComplete}
                        onClick={() =>
                            markComplete({
                                variables: {
                                    override_id: integration.override_id,
                                    update: { status: 'COMPLETE' },
                                },
                            })
                        }
                    >
                        Mark Complete
                    </Button>
                ) : null}
                {removeButton}
            </div>

            {!isEdi ? configBody : null}

            {!isEdi && configFields ? (
                <div>
                    <Button
                        loading={saving}
                        disabled={saving || configFields.some((f) => f.type === 'jsonb' && !isJsonbValid(f.key))}
                        onClick={() => callSaveFunction()}
                    >
                        Save
                    </Button>
                </div>
            ) : null}
        </div>
    );
};

// onSaved and onDiscard mark an unsaved draft: it isn't in the database until its first save.
// onWritten hands the draft the id of a row a failed save still wrote, so the retry updates that row.
const IntegrationRow = ({ integration, onSaved, onDiscard, onWritten }) =>
    integration.type === 'MANUAL' ? (
        <ManualSetupRow integration={integration} onSaved={onSaved} onDiscard={onDiscard} onWritten={onWritten} />
    ) : (
        <ConfiguredIntegrationRow
            integration={integration}
            onSaved={onSaved}
            onDiscard={onDiscard}
            onWritten={onWritten}
        />
    );

export default IntegrationRow;
